import { useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormProvider, useForm } from 'react-hook-form';

import useErrorTranslation from '@/utils/hooks/language/useErrorTranslation';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { post } from '@/utils/client';
import { useSignUpStore } from '@/utils/zustand/providers/sign-up-provider';
// Hooks
import useStateWithReset from '@/utils/hooks/utils/useStateWithReset';

// Types
import type { EmailType } from '@/types/brandedTypes/email';

// Components
import InfoBanner from '@/components/base/InfoBanner';
import SignUpMethodChoiceForm from '@/components/pages/signup/createAccount/SignUpForm/SignUpMethodChoiceForm';
import SignUpWithEmailForm from '@/components/pages/signup/createAccount/SignUpForm/SignUpWithEmailForm';
import type { PasswordFieldFormType } from '@/components/pages/signup/PasswordField';

// Endpoints
import { REGISTER } from '@/routes/apiEndpoints';

export type SignUpFormPropsType = {
    isWithEmail: boolean;
    setIsWithEmail: (arg: boolean) => void;
};

export type SignUpFormType = Pick<PasswordFieldFormType, 'password'> & {
    email: EmailType;
    newsletter: boolean;
    terms: boolean;
};

const SignUpForm = ({ isWithEmail, setIsWithEmail }: SignUpFormPropsType) => {
    const { t, lang } = useTranslation('signup_funnel');
    const { t_error } = useErrorTranslation();
    const { mutateUser } = useUser();
    const { updateCurrentStep, currentStep } = useSignUpStore((state) => state);
    const [globalError, setGlobalError, resetError] = useStateWithReset<string>();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const formMethods = useForm<SignUpFormType>();
    const { setError, handleSubmit } = formMethods;

    useEffect(() => {
        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'signup_visited'
            });
        }
    }, []);

    /* Function to submit form */
    const onSubmit = async (data: SignUpFormType) => {
        resetError();

        try {
            if (typeof executeRecaptcha !== 'function') {
                throw new Error('execute_recaptcha_not_function');
            }
            const token = await executeRecaptcha('signup_page');
            await post(REGISTER, {
                ...data,
                token,
                settings: {
                    platform_language: lang,
                    timezone: 'Europe/Paris'
                }
            });
            if (currentStep) {
                updateCurrentStep(null);
            }
            await mutateUser();
        } catch (e) {
            // Error code 422 = error in form => fields errors
            if (e.status === 422) {
                const errorsFields = e.response?.data?.errors as Record<
                    | keyof SignUpFormType
                    | 'token'
                    | 'settings.timezone'
                    | 'settings.platform_language',
                    string[]
                >;
                // Parse error object, get first error and display errors under each field
                if (errorsFields) {
                    Object.keys(errorsFields).forEach((field) => {
                        const error_type = errorsFields[field][0];
                        /*
                            Display global error if error come from the captcha or display error
                            under each field
                        */
                        if (field === 'token') {
                            setGlobalError(t('create_account.error_recaptacha'));
                        } else if (
                            field === 'settings.timezone' ||
                            field === 'settings.platform_language'
                        ) {
                            setGlobalError(t('errors:error_occured_submit_data'));
                        } else {
                            setError(field, {
                                type: 'manual',
                                message: t_error('errors:error_' + field + '_' + error_type)
                            });
                        }
                    });
                } else {
                    setGlobalError(t('errors:error_occured_submit_data'));
                }
            } else if (e?.message === 'execute_recaptcha_not_function') {
                setGlobalError(t('errors:error_occurred_google_captcha_token'));
            } else {
                setGlobalError(t('errors:error_occured_submit_data'));
            }
        }
    };

    return (
        <FormProvider {...formMethods}>
            <form
                className="mx-auto flex w-full flex-1 flex-col justify-center md:max-w-md"
                onSubmit={handleSubmit(onSubmit)}>
                {globalError ? (
                    <InfoBanner
                        className="mb-5"
                        title={t('errors:error')}
                        message={globalError}
                        onClose={resetError}
                    />
                ) : null}
                {isWithEmail ? (
                    <SignUpWithEmailForm />
                ) : (
                    <SignUpMethodChoiceForm setIsWithEmail={setIsWithEmail} />
                )}
            </form>
        </FormProvider>
    );
};

export default SignUpForm;
